import { DuAnCongViecTienDoTree, DuAnCongViecTree } from '@/types/action-plan/DuAnView';
import { DuAnCongViec } from '@/types/action-plan/ActionPlan/DuAnCongViec';
import { DuAnCongViecDTO } from '@/types/action-plan/ActionPlan/DuAnCongViecDTO';
import { RoleItemTree } from '@/types/action-plan/DuAnView';
import { actionPlanDatesFromDisplayRange, toActionPlanCalendarDate } from '@/utils/dateOnlyForApi';
import { CONSTANTS } from '@/utils/constants';
import { DEFAULT_TIEN_DO_TREE } from '@/components/VanHoa/ActionPlan/ActionPlan/defaultModels';
import { getCongViecTiTrongTarget } from '../../utils/du-an-utils';

/** Form `nv` (string | string[]) to comma-separated IDs for `nguoiPhuTrachID`. */
export function joinNguoiPhuTrachFormValue(
  nv: string | string[] | undefined | null
): string {
  if (nv == null || nv === '') return '';
  if (Array.isArray(nv)) return nv.filter((id) => id && String(id).trim()).join(',');
  const s = String(nv).trim();
  if (!s) return '';
  return s;
}

/** `nguoiPhuTrachID` stored as comma-separated -> list of ids. */
export function splitNguoiPhuTrachIds(raw: string | null | undefined): string[] {
  if (raw == null || raw === '') return [];
  return String(raw).split(',').map((x) => x.trim()).filter(Boolean);
}

/** First id when value is array or comma-separated string (child defaults, single-select row). */
export function firstPhuTrachIdForChildDefault(raw: unknown): string {
  if (raw == null || raw === '') return '';
  if (Array.isArray(raw)) return raw[0] != null ? String(raw[0]) : '';
  const s = String(raw).trim();
  if (!s) return '';
  if (s.includes(',')) return s.split(',')[0].trim() || '';
  return s;
}

// Interface cho childsColumns từ form
export interface ColumnItem {
  id: number | string; // Có thể là number (từ backend) hoặc string (tempxxx cho item mới)
  projectName: string;
  person: string;
  dateTime: string;
  viTri?: number; // Vị trí của công việc con
}

// Hàm check xem id có phải là id mới không (theo logic backend: null, "temp*", "0")
export const isNewId = (id: string | number | null | undefined): boolean => {
  if (id == null || id === undefined || id === '') return true;
  const idStr = id.toString();
  if (idStr.startsWith('temp') || idStr === '0') return true;
  return false;
};

// Hàm convert duAnCongViec để đảm bảo tất cả field required có giá trị
export const convertDuAnCongViec = (duAnCongViec: DuAnCongViecTree): DuAnCongViecTree => {
  return {
    ...duAnCongViec,
    param: duAnCongViec.param || '',
    trongKy: {
      ...duAnCongViec.trongKy,
      icon: duAnCongViec.trongKy?.icon || '',
      titleIcon: duAnCongViec.trongKy?.titleIcon || '',
      giaiDoan: duAnCongViec.trongKy?.giaiDoan || '',
      tienDoHomNay: duAnCongViec.trongKy?.tienDoHomNay || 0,
      hoanThanh: duAnCongViec.trongKy?.hoanThanh || 0,
      trangThai: duAnCongViec.trongKy?.trangThai || 1,
    },
     idTemp: duAnCongViec.idTemp || '',
    childs: duAnCongViec.childs?.map((child: DuAnCongViecTree) => convertDuAnCongViec(child)) || [],
  };
};

const roundTiTrong2 = (n: number): number => Math.round(n * 100) / 100;

/** Con đã có tiTrong nhập tay (không chia lại tiTrongCal). */
const hasManualTiTrong = (cv: DuAnCongViecDTO): boolean =>
  cv.tiTrong !== null &&
  cv.tiTrong !== undefined &&
  Number.isFinite(Number(cv.tiTrong));

// Hàm convert childsColumns sang DuAnCongViecTree[] — giữ con cũ, thêm con mới (temp)
export const convertChildsColumnsToDuAnCongViecTree = (
  childsColumns: ColumnItem[] | undefined,
  existingChilds?: DuAnCongViecTree[] | undefined,
  baseCongViec?: DuAnCongViec,
  fromDuAn: boolean = true,
): DuAnCongViecTree[] => {
  const safeExisting = Array.isArray(existingChilds) ? existingChilds : [];
  if ((!childsColumns || childsColumns.length === 0) && safeExisting.length === 0) {
    return [];
  }

  const result: DuAnCongViecTree[] = [];
  safeExisting.forEach((child) => {
    result.push(convertDuAnCongViec(child));
  });

  const newCols = (childsColumns || []).filter((col) => isNewId(col.id));
  if (newCols.length === 0) {
    return result;
  }

  // Cùng quy ước createNewTask: ngân sách cha; chỉ trừ tiTrong nhập tay; con chỉ có tiTrongCal chia đều lại
  const parentTarget = baseCongViec
    ? getCongViecTiTrongTarget(baseCongViec as DuAnCongViecDTO)
    : 0;
  const parentBudget = parentTarget > 0 ? parentTarget : Number(baseCongViec?.tiTrongCal ?? 100);

  let fixedTotal = 0;
  let autoExistingCount = 0;
  safeExisting.forEach((child) => {
    const cv = child.congViec as DuAnCongViecDTO | undefined;
    if (!cv) return;
    if (hasManualTiTrong(cv)) {
      fixedTotal += Number(cv.tiTrong || 0);
    } else {
      autoExistingCount += 1;
    }
  });

  const remaining = Math.max(0, parentBudget - fixedTotal);
  const autoSlotCount = autoExistingCount + newCols.length;
  const perNewBase =
    autoSlotCount > 0 ? roundTiTrong2(remaining / autoSlotCount) : roundTiTrong2(remaining);
  const tiTrongDiff = roundTiTrong2(remaining - perNewBase * autoSlotCount);

  let maxViTri = -1;
  safeExisting.forEach((child) => {
    const v = child.congViec?.viTri;
    if (typeof v === 'number' && Number.isFinite(v)) {
      maxViTri = Math.max(maxViTri, v);
    }
  });
  let nextViTri = maxViTri + 1;

  newCols.forEach((col, index) => {
    const { ngayBatDau, ngayKetThuc } = actionPlanDatesFromDisplayRange(col.dateTime);

    const thoiGian = col.dateTime || '';
    const isLastNew = index === newCols.length - 1;
    const tiTrongCalNew = fromDuAn ? null : isLastNew ? roundTiTrong2(perNewBase + tiTrongDiff) : perNewBase;
    const viTriNew =
      typeof col.viTri === 'number' && Number.isFinite(col.viTri) ? col.viTri : nextViTri++;

    const newChild: DuAnCongViecTree = {
      congViec: {
        ...baseCongViec,
        parentID: baseCongViec?.id || '',
        id: col.id.toString(),
        tenCongViec: col.projectName || '',
        nguoiPhuTrachID: joinNguoiPhuTrachFormValue(col.person as string | string[]) || '',
        ngayBatDau: ngayBatDau ?? undefined,
        ngayKetThuc: ngayKetThuc ?? undefined,
        viTri: viTriNew,
        tiTrong: fromDuAn ? baseCongViec?.tiTrong || 0 : null,
        tiTrongCal: fromDuAn ? null : tiTrongCalNew,
        moTa: fromDuAn ? null : baseCongViec?.moTa || '',
        filesDinhKem: fromDuAn ? null : baseCongViec?.filesDinhKem,
        mtctid: baseCongViec?.mtctid || '',
        duAnID: baseCongViec?.duAnID || '',
        site: baseCongViec?.site || '',
        kanBanViTri: baseCongViec?.kanBanViTri || 0,
        nguoiHoTroID: '',
        chiTieu: 100,
        nganSach: 0,
        nganSachCal: 0,
        tanSuat: CONSTANTS.ACTION_PLAN.TABLE.TANSUAT.BAO_CAO_CUOI_KY,
        hinhThucTongHop: CONSTANTS.ACTION_PLAN.TABLE.HINH_THUC_TONG_HOP.NHAP_TAY,
        phuTrachMucTieu: baseCongViec?.phuTrachMucTieu || '',
        idDoLuong: baseCongViec?.idDoLuong || '',
        year: baseCongViec?.year || undefined,
        kanbans: (baseCongViec as any)?.kanbans || [],
        level: Number((baseCongViec as DuAnCongViecDTO)?.level ?? 0) + 1,
        hienThiTrangQuanLy: baseCongViec?.hienThiTrangQuanLy ?? true,
      } as unknown as DuAnCongViecDTO,
      thoiGian: thoiGian,
      hasChange: true,
      hasInRange: false,
      hasChild: false,
      param: '',
      trongKy: DEFAULT_TIEN_DO_TREE as DuAnCongViecTienDoTree,
      luyke:  DEFAULT_TIEN_DO_TREE as DuAnCongViecTienDoTree,
      childs: [],
      countChilds: 0,
      tenTanSuat: '',
      tenHinhThucTinh: '',
      roleItemTree: {} as RoleItemTree,
      tiTrongDaAn: 0,
      tiTrongTatCaDaAn: 0,
      idTemp: '',
    };

    result.push(convertDuAnCongViec(newChild));
  });

  return result;
};

